import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Phone, CalendarHeart } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { useLocation } from "@/contexts/LocationContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { getLocationPhotos } from "@/lib/media";

export function WarmPhotoCarousel() {
  const { location, isSeattle } = useLocation();
  const isMobile = useIsMobile();
  // Real stills only (from the home walkthrough video). Seattle has none yet.
  const photos = getLocationPhotos(location.id);
  const basePath = isSeattle ? "/seattle" : "";

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    duration: 30, // Embla uses frame-based duration, ~650ms
    align: "start",
    slidesToScroll: 1,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track selected slide
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi, onSelect]);

  // Intersection Observer for visibility + entrance animation
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated]);

  // Auto-play logic
  const startAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 4000);
  }, [emblaApi]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    if (isVisible && !isHovered) {
      startAutoplay();
    } else {
      stopAutoplay();
    }
    return stopAutoplay;
  }, [emblaApi, isVisible, isHovered, startAutoplay, stopAutoplay]);

  const handleInteractionStart = useCallback(() => {
    setIsHovered(true);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  }, []);

  const handleInteractionEnd = useCallback(() => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => setIsHovered(false), 3000);
  }, []);

  // No stock, ever. If a location has no real photos yet (Seattle), show an
  // honest invitation to tour in person instead of a fabricated gallery.
  if (!photos.length) {
    return (
      <section className="pt-4 pb-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl rounded-3xl border border-border bg-card p-10 text-center shadow-[var(--shadow-soft)]">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/15 text-accent-strong">
              <CalendarHeart className="h-6 w-6" />
            </div>
            <h2 className="mt-5 display-md font-heading font-semibold tracking-[-0.025em] text-foreground">
              See our {location.shortName} home{" "}
              <span className="font-display-italic font-normal text-accent-strong">in person</span>
            </h2>
            <p className="mx-auto mt-3 max-w-md text-muted-foreground">
              We&rsquo;d rather show you the real thing than a stock photo. Call us and
              we&rsquo;ll arrange a visit at a time that works for your family.
            </p>
            <a
              href={`tel:${location.primaryPhone.replace(/-/g, "")}`}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-md transition-[transform,box-shadow] duration-200 ease-out-expo hover:shadow-lg active:scale-[0.98]"
            >
              <Phone className="h-4 w-4" />
              Call {location.primaryPhone}
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={`pt-20 pb-16 md:pt-28 md:pb-20 transition-all duration-700 ${
        hasAnimated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div className="container mx-auto px-4">
        <h2 className="text-center display-md font-heading font-semibold tracking-[-0.025em] text-foreground">
          Life at{" "}
          <span className="font-display-italic font-normal text-accent-strong">our home</span>
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-muted-foreground">
          Glimpses of the warm, loving environment where our residents thrive.
        </p>
      </div>

      {/* Carousel */}
      <div
        className="group relative mt-8 mx-auto max-w-6xl px-4"
        onMouseEnter={handleInteractionStart}
        onMouseLeave={handleInteractionEnd}
        onTouchStart={handleInteractionStart}
        onTouchEnd={handleInteractionEnd}
      >
        <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
          <div className="flex">
            {photos.map((photo, index) => (
              <div
                key={index}
                className="flex-[0_0_100%] min-w-0 px-2 md:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
              >
                <div className="relative h-64 md:h-80 overflow-hidden rounded-2xl shadow-[var(--shadow-card)]">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    loading={index < 3 ? "eager" : "lazy"}
                    className="h-full w-full object-cover"
                  />
                  {/* Label overlay */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <span className="text-sm font-medium text-white">{photo.label}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Arrow buttons - hidden on mobile */}
        {!isMobile && (
          <>
            <button
              onClick={() => emblaApi?.scrollPrev()}
              className="absolute left-6 top-1/2 -translate-y-1/2 hidden md:flex h-10 w-10 items-center justify-center rounded-full bg-white/70 backdrop-blur-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white/90"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5 text-foreground" />
            </button>
            <button
              onClick={() => emblaApi?.scrollNext()}
              className="absolute right-6 top-1/2 -translate-y-1/2 hidden md:flex h-10 w-10 items-center justify-center rounded-full bg-white/70 backdrop-blur-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white/90"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5 text-foreground" />
            </button>
          </>
        )}
      </div>

      {/* Dot indicators */}
      <div className="mt-6 flex justify-center gap-2">
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            onClick={() => emblaApi?.scrollTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === selectedIndex
                ? "w-6 bg-primary"
                : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* View Gallery link */}
      <div className="mt-6 text-center">
        <Link
          to={`${basePath}/gallery`}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent-strong transition-colors"
        >
          View Full Gallery
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}
